import { useSyncExternalStore } from "react"

export interface UseIsMobileOptions {
  /**
   * When the *shorter* viewport edge is at or below this (in CSS px), treat as
   * mobile. This catches phones in landscape, where the longer edge often
   * exceeds `mobileScreenSize` but the short edge stays phone-sized (~360–500).
   * @default 600
   */
  shortEdgeMax?: number
}
const mobileScreenSize = 768
const shortEdgeMax = 600

export function computeIsMobile(): boolean {
  if (
    typeof window === "undefined" ||
    typeof window.matchMedia !== "function"
  ) {
    return false
  }

  const narrow = window.matchMedia(`(max-width: ${mobileScreenSize}px)`).matches
  const w = window.innerWidth
  const h = window.innerHeight
  const shortSide = Math.min(w, h)
  return narrow || shortSide <= shortEdgeMax
}

export function useIsMobile() {
  if (mobileScreenSize < 0) {
    throw new TypeError("mobileScreenSize must be a positive number")
  }
  if (shortEdgeMax < 0) {
    throw new TypeError("shortEdgeMax must be a positive number")
  }

  return useSyncExternalStore(
    onStoreChange=> {
      if (
        typeof window === "undefined" ||
        typeof window.matchMedia !== "function"
      ) {
        return ()=> {}
      }

      const onChange = ()=> onStoreChange()
      const mqNarrow = window.matchMedia(`(max-width: ${mobileScreenSize}px)`)

      window.addEventListener("resize", onChange)
      window.addEventListener("orientationchange", onChange)
      try {
        mqNarrow.addEventListener("change", onChange)
      } catch {
        mqNarrow.addListener(onChange)
      }

      return ()=> {
        window.removeEventListener("resize", onChange)
        window.removeEventListener("orientationchange", onChange)
        try {
          mqNarrow.removeEventListener("change", onChange)
        } catch {
          mqNarrow.removeListener(onChange)
        }
      }
    },
    ()=> computeIsMobile(),
    ()=> false,
  )
}
